import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Server, Check, RefreshCw, ChevronDown } from "lucide-react";
import { useAuthStore } from "../stores/authStore";
import EmailSpinner from "../components/mail/EmailSpinner";
import AppLogo from "../components/mail/AppLogo";
import {
  getApiBaseUrl,
  getCustomApiBaseUrl,
  setCustomApiBaseUrl,
  DEFAULT_API_URL,
} from "../utils/apiConfig";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showServerConfig, setShowServerConfig] = useState(false);

  // Servidor y endpoints
  const [currentApiUrl, setCurrentApiUrl] = useState(getApiBaseUrl());
  const [serverInput, setServerInput] = useState(getCustomApiBaseUrl() || DEFAULT_API_URL);
  const [serverSavedSuccess, setServerSavedSuccess] = useState(false);

  const { login, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch {
      // Error is handled by the store
    }
  };

  const handleSaveServer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!serverInput.trim()) {
      setCustomApiBaseUrl(null);
    } else {
      setCustomApiBaseUrl(serverInput.trim());
    }
    const updated = getApiBaseUrl();
    setCurrentApiUrl(updated);
    setServerSavedSuccess(true);
    setTimeout(() => setServerSavedSuccess(false), 2000);
  };

  const handleResetDefaultServer = () => {
    setCustomApiBaseUrl(null);
    setServerInput(DEFAULT_API_URL);
    setCurrentApiUrl(DEFAULT_API_URL);
    setServerSavedSuccess(true);
    setTimeout(() => setServerSavedSuccess(false), 2000);
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--bg-primary)] px-4 py-8">
      <div className="w-full max-w-[400px]">
        {/* Logo y Encabezado Limpio */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-3">
            <AppLogo size={56} />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
            <span className="text-accent">Magic Mail</span>
          </h1>
          <p className="text-sm mt-1 text-[var(--text-muted)] font-medium">Cliente de Correo Electrónico</p>
        </div>

        {/* Formulario de Login */}
        <form onSubmit={handleSubmit} className="space-y-4 bg-[var(--bg-primary)] border border-[var(--border-color)] p-6 shadow-sm">
          {error && (
            <div className="px-3 py-2 text-xs bg-red-500/10 border border-red-500/20 text-red-500 font-medium">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-[var(--text-muted)]">
              Correo electrónico
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearError(); }}
              className="w-full px-3.5 py-2.5 text-sm border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder-[var(--text-muted)] outline-none focus:border-accent transition-colors"
              placeholder="usuario@dominio.com"
              autoFocus
              autoComplete="username"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-[var(--text-muted)]">
              Contraseña
            </label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError(); }}
                className="w-full px-3.5 py-2.5 pr-10 text-sm border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder-[var(--text-muted)] outline-none focus:border-accent transition-colors"
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                title={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
                aria-label={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !email.trim() || !password.trim()}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-white text-sm font-semibold bg-accent hover:bg-accent-hover transition-all shadow-sm disabled:opacity-75 disabled:cursor-not-allowed mt-2"
          >
            {isLoading ? (
              <>
                <EmailSpinner size={18} className="text-white" />
                <span className="text-white font-medium">Iniciando sesión...</span>
              </>
            ) : (
              <>
                <span>Iniciar Sesión</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        {/* Sección de configuración de Endpoint / Servidor */}
        <div className="mt-4 bg-[var(--bg-primary)] border border-[var(--border-color)] p-4 shadow-sm">
          <button
            type="button"
            onClick={() => setShowServerConfig(!showServerConfig)}
            className="w-full flex items-center justify-between text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Server size={14} className="text-accent" />
              <span>Servidor / Endpoint API</span>
            </div>
            <div className="flex items-center gap-1.5 text-2xs font-mono text-[var(--text-muted)]">
              <span className="max-w-[170px] truncate">{currentApiUrl}</span>
              <ChevronDown
                size={14}
                className={`transition-transform duration-200 ${showServerConfig ? "rotate-180" : ""}`}
              />
            </div>
          </button>

          {showServerConfig && (
            <div className="mt-4 pt-4 border-t border-[var(--border-color)] space-y-3">
              <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                Por defecto se conecta al servidor oficial de Magic Mail y acepta automáticamente cualquier dominio de correo registrado. Si utilizas un servidor propio, puedes personalizar la URL aquí.
              </p>

              <div>
                <label className="block text-2xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                  URL del Servidor (Endpoint API)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={serverInput}
                    onChange={(e) => setServerInput(e.target.value)}
                    placeholder="https://correo.mi-empresa.com/api"
                    className="flex-1 px-3 py-1.5 text-xs font-mono border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] outline-none focus:border-accent"
                  />
                  <button
                    type="button"
                    onClick={handleSaveServer}
                    className="px-3 py-1.5 text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors flex items-center gap-1"
                  >
                    {serverSavedSuccess ? <Check size={14} /> : "Guardar"}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={handleResetDefaultServer}
                  className="text-2xs text-[var(--text-muted)] hover:text-accent underline flex items-center gap-1"
                >
                  <RefreshCw size={11} />
                  Restaurar servidor por defecto
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs mt-6 text-[var(--text-muted)]">
          Magic Mail · Seguridad y Privacidad
        </p>
      </div>
    </div>
  );
}
